import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE, getBearer } from "../lib/api";
import { extractError } from "../lib/format";
import { Award, Download, ExternalLink, Loader2 } from "lucide-react";
import ShareRow from "./ShareRow";

const TYPE_LABEL = {
  level_1: "Term Completion",
  level_2: "Age-Band Graduate",
};
const GRADE_LABEL = {
  distinction: "Distinction",
  merit: "Merit",
  pass: "Pass",
};

function bandLabel(id) {
  if (!id) return "";
  return "Ages " + id.replace("age_", "").replace("-", "\u2013");
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function MyCertificates() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/certificates")
      .then(({ data }) => {
        if (alive) setItems(data.items || []);
      })
      .catch((e) => {
        if (alive) setErr(extractError(e, "Could not load certificates."));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const downloadPdf = async (slug) => {
    try {
      const res = await fetch(`${API_BASE}/api/certificates/${encodeURIComponent(slug)}/download`, {
        headers: { Authorization: `Bearer ${getBearer()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, "Could not download the PDF."));
    }
  };

  if (loading) {
    return (
      <div className="ftg-subs__empty" data-testid="certs-loading">
        <Loader2 size={18} className="ftg-spin" /> Loading your certificates…
      </div>
    );
  }
  if (err) {
    return <div className="ftg-subs__empty" data-testid="certs-error">{err}</div>;
  }
  if (!items.length) {
    return (
      <div className="ftg-subs__empty" data-testid="certs-empty">
        No certificates yet. Complete a full term (all 20 lessons + at least one homework submission) to earn your first one.
      </div>
    );
  }

  return (
    <div className="ftg-certs" data-testid="certs-list">
      {items.map((c) => {
        const isL2 = c.cert_type === "level_2";
        const verifyUrl = `${window.location.origin}/verify/${encodeURIComponent(c.cert_id_slug)}`;
        return (
          <div
            key={c.id}
            className={`ftg-cert-card${isL2 ? " is-graduate" : ""}`}
            data-testid={`cert-card-${c.cert_id_slug}`}
          >
            <div className="ftg-cert-card__badge">
              <Award size={28} />
            </div>
            <div className="ftg-cert-card__body">
              <div className="ftg-cert-card__eyebrow">{TYPE_LABEL[c.cert_type] || c.cert_type}</div>
              <div className="ftg-cert-card__title">
                {isL2 ? bandLabel(c.age_band) : `Term ${c.term} · ${bandLabel(c.age_band)}`}
              </div>
              <div className="ftg-cert-card__meta">
                Issued {formatDate(c.issued_at)} · <span className="ftg-cert-card__slug">{c.cert_id_slug}</span>
              </div>
              {c.grade && (
                <span className={`ftg-cert-card__grade ftg-cert-card__grade--${c.grade}`}>
                  {GRADE_LABEL[c.grade] || c.grade}
                </span>
              )}
            </div>
            <div className="ftg-cert-card__cta">
              <button
                type="button"
                onClick={() => downloadPdf(c.cert_id_slug)}
                className="ftg-btn ftg-btn--primary"
                data-testid={`cert-download-${c.cert_id_slug}`}
              >
                <Download size={16} /> Download PDF
              </button>
              <Link
                to={`/verify/${encodeURIComponent(c.cert_id_slug)}`}
                className="ftg-btn ftg-btn--ghost"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cert-verify-${c.cert_id_slug}`}
              >
                <ExternalLink size={14} /> Verify link
              </Link>
              <ShareRow
                cert={c}
                verifyUrl={verifyUrl}
                variant="compact"
                testIdPrefix={`cert-share-${c.cert_id_slug}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
